import {
  Box,
  Button,
  Divider,
  Popover,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useEffect, useState } from "react";
import { reviewTokens } from "../../theme";
import {
  isoFromHoursFromNow,
  isoToLocalInput,
  localInputToIso,
} from "./dateInput";

interface Props {
  open: boolean;
  anchorEl: HTMLElement | null;
  currentExpiresAt: string | Date | null | undefined;
  onClose: () => void;
  onConfirm: (expiresAtIso: string | null) => Promise<void> | void;
}

const PRESETS: { label: string; hours: number | null }[] = [
  { label: "24 hours", hours: 24 },
  { label: "7 days", hours: 24 * 7 },
  { label: "30 days", hours: 24 * 30 },
  { label: "Never", hours: null },
];

export function RegenerateLinkPopover({
  open,
  anchorEl,
  currentExpiresAt,
  onClose,
  onConfirm,
}: Props) {
  const [value, setValue] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(isoToLocalInput(currentExpiresAt));
      setSubmitting(false);
    }
  }, [open, currentExpiresAt]);

  const applyPreset = (hours: number | null) => {
    if (hours === null) {
      setValue("");
      return;
    }
    setValue(isoToLocalInput(isoFromHoursFromNow(hours)));
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const iso = localInputToIso(value);
      await onConfirm(iso ?? null);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{
        sx: {
          width: 320,
          p: 2,
          borderRadius: `${reviewTokens.radiusMd}px`,
          border: `1px solid ${reviewTokens.border}`,
          boxShadow:
            "0 8px 28px rgba(15, 23, 42, 0.10), 0 2px 6px rgba(15, 23, 42, 0.06)",
        },
      }}
    >
      <Stack spacing={1.5}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Regenerate access link
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Issues a new URL and invalidates the previous one.
          </Typography>
        </Box>

        <TextField
          size="small"
          type="datetime-local"
          label="Expires at"
          InputLabelProps={{ shrink: true }}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          helperText={
            value ? "Token will expire at this time." : "Token will never expire."
          }
          fullWidth
        />

        <Box>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mb: 0.5,
              fontWeight: 600,
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "text.secondary",
            }}
          >
            Quick set
          </Typography>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            {PRESETS.map((p) => (
              <Button
                key={p.label}
                size="small"
                variant="outlined"
                onClick={() => applyPreset(p.hours)}
                sx={{
                  minWidth: 0,
                  px: 1,
                  py: 0.25,
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: "none",
                  borderColor: reviewTokens.border,
                  color: "text.secondary",
                  "&:hover": {
                    borderColor: reviewTokens.borderStrong,
                    bgcolor: reviewTokens.slate50,
                  },
                }}
              >
                {p.label}
              </Button>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: reviewTokens.border }} />

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button
            size="small"
            onClick={onClose}
            disabled={submitting}
            sx={{ textTransform: "none", color: "text.secondary" }}
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            disabled={submitting}
            startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: "none" }}
          >
            Regenerate
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
}
